import React, { useContext, useState, useEffect } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";

import MUIButton from "@mui/material/Button";
import MUIGrid from "@mui/material/Grid";
import MUITextField from "@mui/material/TextField";
import MUIInputAdornment from "@mui/material/InputAdornment";
import MUITypography from "@mui/material/Typography";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIListIcon from "@mui/material/ListItemIcon";
import MUIAddIcon from "@mui/icons-material/Add";
import MUISaveIcon from "@mui/icons-material/Save";
import MUIDeleteIcon from "@mui/icons-material/Delete";
import MUIStraightenIcon from "@mui/icons-material/Straighten";
import MUIBox from "@mui/material/Box";

import Menu from "../../Menu";
import Card from "../../Card";
import DialogConfirm from "../../Dialog/DialogConfirm";
import EmptyState from "../../EmptyState";
import measurementsInSittingImage from "../../../Asset/Image/measurements_in_sitting.png";
import Setting from "../../../Utility/Setting";

/**
 * CardChartMeasurementInSitting renders a Card component
 * displaying order measurements information.
 * @returns {JSX.Element}
 */
const className = "CardChartMeasurementInSitting";
const title = "Measurements In Sitting";
const icon = MUIStraightenIcon;

export default function CardChartMeasurementInSitting(props) {
  const chartContext = useContext(ChartContext);
  const atpAssessment =
    Object.values(chartContext.chart.atpAssessment).find((atpAssessment) => {
      return atpAssessment.orderActivityId === chartContext.orderId;
    }) || [];
  const measurementsInSitting =
    Object.values(chartContext.chart.measurementInSitting).find(
      (measurement) => {
        return measurement.orderActivityId === chartContext.orderId;
      },
    ) || [];
  const originalMeasurementInSitting =
    Object.values(chartContext.originalChart.measurementInSitting).find(
      (measurement) => {
        return measurement.orderActivityId === chartContext.orderId;
      },
    ) || [];
  const [editMode, setEditMode] = useState(props.startInEditMode ?? false);
  const [cardChart, setCardChart] = useState(null);
  const [pendingSave, setPendingSave] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [imageFilters, setImageFilters] = useState("");

  useEffect(() => {
    if (editMode && atpAssessment.id) {
      chartContext.onChange({
        atpAssessment: {
          [atpAssessment.id]: {
            measurementInSittingId: measurementsInSitting?.id,
          },
        },
      });
    }
  }, [measurementsInSitting.id, atpAssessment.id]);

  useEffect(() => {
    const handleCreateMeasurements = () => {
      const newMeasurementId = chartContext.getNegativeId();
      const measurements = {
        [newMeasurementId]: {
          id: newMeasurementId,
          patientId: chartContext.patientId,
          orderActivityId: chartContext.orderId,
          createdById: 1,
          deleted: false,
        },
      };
      chartContext.onChange({ measurementInSitting: measurements });
      setEditMode(true);
    };
    if (!measurementsInSitting.id) {
      handleCreateMeasurements();
    }
  }, [measurementsInSitting.id]);

  useEffect(() => {
    const measurementKeys = [
      { key: "topOfHead" },
      { key: "topOfShoulderLeft" },
      { key: "topOfShoulderRight" },
      { key: "axillaLeft" },
      { key: "axillaRight" },
      { key: "seatDepthLeft" },
      { key: "seatDepthRight" },
      { key: "lowerLegLengthLeft" },
      { key: "lowerLegLengthRight" },
      { key: "footLengthLeft" },
      { key: "footLengthRight" },
      { key: "footWidthLeft" },
      { key: "footWidthRight" },
      { key: "seatToElbowLeft" },
      { key: "seatToElbowRight" },
      { key: "hipWidth" },
      { key: "chestWidth" },
      { key: "shoulderWidth" },
      { key: "headWidth" },
      { key: "headDepth" },
      { key: "neckCircumference" },
      { key: "inferiorAngleOfScapula" },
      { key: "chestDepth" },
    ];

    if (editMode) {
      chartContext.updateValidationRules({
        key: className,
        rules: measurementKeys.map(({ key }) => {
          return {
            target: "measurementInSitting",
            id: measurementsInSitting.id,
            field: key,
            rule: {
              type: "number",
              min: 0,
              max: 99.9,
            },
          };
        }),
      });
    } else {
      chartContext.updateValidationRules({ key: className, rules: [] });
    }
  }, [editMode, chartContext.chart.measurementInSitting]);

  // Sync this card's edit mode with ChartContext for ChartNavigationBar display
  useEffect(() => {
    chartContext.setCardEditMode?.(className, editMode);
    return () => chartContext.setCardEditMode?.(className, false);
  }, [editMode, chartContext.setCardEditMode]);

  useEffect(() => {
    const filters = Setting.get("ui.theme") === "dark" ? "invert(100%)" : "";
    setImageFilters(filters);

    const handleThemeChange = () => {
      const filters = Setting.get("ui.theme") === "dark" ? "invert(100%)" : "";
      setImageFilters(filters);
    };

    window.addEventListener("setting.ui.theme", handleThemeChange);

    return () => {
      window.removeEventListener("setting.ui.theme", handleThemeChange);
    };
  }, []);

  // Close edit mode if originalChart changes (on save)
  useEffect(() => {
    setEditMode(props.startInEditMode ?? false);
  }, [chartContext.originalChart]);

  /**
   * Formats a measurement value to one decimal place, or returns null if
   * invalid.
   * @param {string|number} value - The value to format.
   * @returns {number|null}
   */
  const formatMeasurement = (value) => {
    if (
      value === "" ||
      value === null ||
      value === undefined ||
      isNaN(Number(value))
    )
      return null;
    const numValue = Math.round(Number(value) * 10) / 10;
    return numValue;
  };

  /**
   * Checks if a measurement value is valid.
   * @param {string|number|null|undefined} value - The value to check.
   * @returns {boolean}
   */
  function hasMeasurementValue(value) {
    return value !== null && value !== undefined && value !== "";
  }

  /**
   * Cleans and formats a measurement value.
   * @param {string|number|null|undefined} value - The value to clean.
   * @returns {number|null}
   */
  function cleanMeasurementValue(value) {
    if (value === null || value === undefined) return null;
    const trimmed = String(value).trim();
    const numValue = parseFloat(trimmed);
    if (/^[0-9]{2}\.[0-9]{0,1}$/.test(trimmed)) {
      return numValue;
    } else {
      return formatMeasurement(numValue);
    }
  }

  /**
   * Renders a row of measurement values (read-only or editable).
   * @param {Object} row - Measurement row definition.
   * @param {string|number} valueLeft - Left value.
   * @param {string|number} valueRight - Right value.
   * @returns {JSX.Element}
   */
  function renderMeasurementRow(row, valueLeft, valueRight) {
    const labelGridSx = { flexBasis: "58%", maxWidth: "58%" };
    const measurementGridSx = {
      textAlign: "right",
      flexBasis: "18%",
      maxWidth: "18%",
    };
    const spacerGridSx = { flexBasis: "6%", maxWidth: "6%" };

    return (
      <React.Fragment key={row.label}>
        <MUIGrid item sx={labelGridSx}>
          <MUIGrid container wrap="nowrap">
            <MUIGrid item xs={1}>
              <MUITypography variant="subtitle2">{row.letter}</MUITypography>
            </MUIGrid>
            <MUIGrid item xs={11}>
              <MUITypography variant="subtitle2">
                {row.label || ""}
              </MUITypography>
            </MUIGrid>
          </MUIGrid>
        </MUIGrid>
        <MUIGrid item sx={measurementGridSx}>
          {editMode ? (
            <MUITextField
              value={hasMeasurementValue(valueLeft) ? valueLeft : ""}
              size="small"
              variant="standard"
              fullWidth
              inputProps={{ maxLength: 4, style: { textAlign: "right" } }}
              InputProps={{
                endAdornment: (
                  <MUIInputAdornment position="end">
                    {row.adornment}
                  </MUIInputAdornment>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              onBlur={(e) =>
                chartContext.onChange({
                  measurementInSitting: {
                    [measurementsInSitting.id]: {
                      [row.left]: cleanMeasurementValue(e.target.value),
                    },
                  },
                })
              }
              onChange={(e) => {
                let value = e.target.value.replace(/[^0-9.]/g, "");
                const parts = value.split(".");
                if (parts.length > 2) {
                  value = parts[0] + "." + parts.slice(1).join("");
                } else if (value === "") {
                  value = null;
                }
                chartContext.onChange({
                  measurementInSitting: {
                    [measurementsInSitting.id]: { [row.left]: value },
                  },
                });
                setCardChart({
                  ...cardChart,
                  [row.left]: cleanMeasurementValue(value),
                });
              }}
              error={
                chartContext.validationIssues[className]
                  ?.measurementInSitting?.[measurementsInSitting.id]?.[row.left]
                  ? true
                  : false
              }
              helperText={
                chartContext.validationIssues[className]
                  ?.measurementInSitting?.[measurementsInSitting.id]?.[row.left]
                  ? "Invalid"
                  : ""
              }
            />
          ) : (
            <MUITypography variant="body2">
              {hasMeasurementValue(valueLeft)
                ? parseFloat(valueLeft).toFixed(1)
                : ""}
              {hasMeasurementValue(valueLeft) ? " " + row.adornment : ""}
            </MUITypography>
          )}
        </MUIGrid>
        <MUIGrid item sx={spacerGridSx}></MUIGrid>
        <MUIGrid item sx={measurementGridSx}>
          {row.right ? (
            editMode ? (
              <MUITextField
                value={hasMeasurementValue(valueRight) ? valueRight : ""}
                size="small"
                variant="standard"
                fullWidth
                inputProps={{ maxLength: 4, style: { textAlign: "right" } }}
                InputProps={{
                  endAdornment: (
                    <MUIInputAdornment position="end">
                      {row.adornment}
                    </MUIInputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                onBlur={(e) =>
                  chartContext.onChange({
                    measurementInSitting: {
                      [measurementsInSitting.id]: {
                        [row.right]: cleanMeasurementValue(
                          e.target.value.trim(),
                        ),
                      },
                    },
                  })
                }
                onChange={(e) => {
                  let value = e.target.value.replace(/[^0-9.]/g, "");
                  const parts = value.split(".");
                  if (parts.length > 2) {
                    value = parts[0] + "." + parts.slice(1).join("");
                  } else if (value === "") {
                    value = null;
                  }
                  chartContext.onChange({
                    measurementInSitting: {
                      [measurementsInSitting.id]: { [row.right]: value },
                    },
                  });
                  setCardChart({
                    ...cardChart,
                    [row.right]: cleanMeasurementValue(value),
                  });
                }}
                error={
                  chartContext.validationIssues[className]
                    ?.measurementInSitting?.[measurementsInSitting.id]?.[
                    row.right
                  ]
                    ? true
                    : false
                }
                helperText={
                  chartContext.validationIssues[className]
                    ?.measurementInSitting?.[measurementsInSitting.id]?.[
                    row.right
                  ]
                    ? "Invalid"
                    : ""
                }
              />
            ) : (
              <MUITypography variant="body2">
                {hasMeasurementValue(valueRight)
                  ? parseFloat(valueRight).toFixed(1)
                  : ""}
                {hasMeasurementValue(valueRight) ? " " + row.adornment : ""}
              </MUITypography>
            )
          ) : null}
        </MUIGrid>
      </React.Fragment>
    );
  }

  /**
   * Renders the primary measurements section.
   * @returns {JSX.Element}
   */
  const renderPrimaryMeasurements = () => {
    const rowsA = [
      {
        key: "topOfHead",
        letter: "A",
        label: "Top of Head",
        left: "topOfHead",
        right: null,
        adornment: "in",
      },
    ];
    const rowsBToH = [
      {
        key: "topOfShoulder",
        letter: "B",
        label: "Top of Shoulder",
        left: "topOfShoulderLeft",
        right: "topOfShoulderRight",
        adornment: "in",
      },
      {
        key: "axilla",
        letter: "C",
        label: "Axilla",
        left: "axillaLeft",
        right: "axillaRight",
        adornment: "in",
      },
      {
        key: "seatDepth",
        letter: "D",
        label: "Seat Depth",
        left: "seatDepthLeft",
        right: "seatDepthRight",
        adornment: "in",
      },
      {
        key: "lowerLegLength",
        letter: "E",
        label: "Lower Leg Length",
        left: "lowerLegLengthLeft",
        right: "lowerLegLengthRight",
        adornment: "in",
      },
      {
        key: "footLength",
        letter: "F",
        label: "Foot Length",
        left: "footLengthLeft",
        right: "footLengthRight",
        adornment: "in",
      },
      {
        key: "footWidth",
        letter: "G",
        label: "Foot Width",
        left: "footWidthLeft",
        right: "footWidthRight",
        adornment: "in",
      },
      {
        key: "seatToElbow",
        letter: "H",
        label: "Seat to Elbow",
        left: "seatToElbowLeft",
        right: "seatToElbowRight",
        adornment: "in",
      },
    ];
    const rowsIToK = [
      {
        key: "hipWidth",
        letter: "I",
        label: "Hip Width",
        left: "hipWidth",
        right: null,
        adornment: "in",
      },
      {
        key: "chestWidth",
        letter: "J",
        label: "Chest Width",
        left: "chestWidth",
        right: null,
        adornment: "in",
      },
      {
        key: "shoulderWidth",
        letter: "K",
        label: "Shoulder Width",
        left: "shoulderWidth",
        right: null,
        adornment: "in",
      },
    ];

    return (
      <MUIGrid container>
        {rowsA.map((row) =>
          renderMeasurementRow(
            row,
            measurementsInSitting[row.left],
            measurementsInSitting[row.right],
          ),
        )}

        <MUIGrid item xs={12}>
          &nbsp;
        </MUIGrid>

        <MUIGrid item xs={7}></MUIGrid>
        <MUIGrid item xs={2}>
          <MUITypography
            variant="body2"
            sx={{
              color: (theme) => theme.palette.primary.main,
              textAlign: "right",
            }}
          >
            Left
          </MUITypography>
        </MUIGrid>
        <MUIGrid item xs={1}></MUIGrid>
        <MUIGrid item xs={2}>
          <MUITypography
            variant="body2"
            sx={{
              color: (theme) => theme.palette.error.main,
              textAlign: "right",
            }}
          >
            Right
          </MUITypography>
        </MUIGrid>

        {rowsBToH.map((row) =>
          renderMeasurementRow(
            row,
            measurementsInSitting[row.left],
            measurementsInSitting[row.right],
          ),
        )}

        <MUIGrid item xs={12}>
          &nbsp;
        </MUIGrid>

        {rowsIToK.map((row) =>
          renderMeasurementRow(row, measurementsInSitting[row.left]),
        )}
      </MUIGrid>
    );
  };

  /**
   * Renders the additional measurements section.
   * @returns {JSX.Element}
   */
  const renderAdditionalMeasurements = () => {
    const rows = [
      {
        key: "headWidth",
        label: "Head Width",
        left: "headWidth",
        right: null,
        adornment: "in",
      },
      {
        key: "headDepth",
        label: "Head Depth",
        left: "headDepth",
        right: null,
        adornment: "in",
      },
      {
        key: "neckWidth",
        label: "Neck Width",
        left: "neckWidth",
        right: null,
        adornment: "in",
      },
      {
        key: "neckCircumference",
        label: "Neck Circumference",
        left: "neckCircumference",
        right: null,
        adornment: "in",
      },
      {
        key: "inferiorAngleOfScapula",
        label: "Inferior Angle of Scapula",
        left: "inferiorAngleOfScapula",
        right: null,
        adornment: "in",
      },
      {
        key: "chestDepth",
        label: "Chest Depth",
        left: "chestDepth",
        right: null,
        adornment: "in",
      },
    ];

    return (
      <MUIGrid container>
        {rows.map((row) =>
          renderMeasurementRow(
            row,
            measurementsInSitting[row.left],
            measurementsInSitting[row.right],
          ),
        )}
      </MUIGrid>
    );
  };

  /**
   * Renders the read-only view of measurements.
   * @returns {JSX.Element}
   */
  const renderReadOnlyView = () => {
    return Object.values(measurementsInSitting).length > 0 ? (
      <MUIGrid container>
        <MUIGrid item xs={9} md={6} lg={4}>
          {renderPrimaryMeasurements()}
        </MUIGrid>
        <MUIGrid item xs={9} md={6} lg={4}>
          {renderAdditionalMeasurements()}
        </MUIGrid>
        <MUIGrid
          item
          xs={12}
          md={12}
          lg={4}
          sx={{ display: { xs: "none", md: "none", lg: "block" } }}
        >
          <img
            src={measurementsInSittingImage}
            alt="Measurement Illustration"
            style={{ width: "100%", filter: imageFilters }}
          />
        </MUIGrid>
        <MUIGrid item xs={12} sx={{ marginTop: 1 }}>
          {editMode ? (
            <MUITextField
              multiline
              rows={4}
              fullWidth={true}
              InputLabelProps={{ shrink: true }}
              inputProps={{ style: { resize: "vertical" } }}
              value={measurementsInSitting["additionalComments"] || ""}
              onBlur={(e) =>
                chartContext.onChange({
                  measurementInSitting: {
                    [measurementsInSitting.id]: {
                      additionalComments: e.target.value.trim() || null,
                    },
                  },
                })
              }
              onChange={(e) => {
                chartContext.onChange({
                  measurementInSitting: {
                    [measurementsInSitting.id]: {
                      additionalComments: e.target.value,
                    },
                  },
                });
                setCardChart({
                  ...cardChart,
                  additionalComments: e.target.value,
                });
              }}
              label={"Additional Comments"}
              variant="standard"
              sx={{ mb: 2 }}
            />
          ) : (
            <MUIBox>
              <MUITypography variant="subtitle2">
                Additional Comments
              </MUITypography>
              <MUITypography variant="body2">
                {measurementsInSitting["additionalComments"] || "None"}
              </MUITypography>
            </MUIBox>
          )}
        </MUIGrid>
      </MUIGrid>
    ) : (
      <EmptyState line1="Nothing to show" line2="Better luck next time!" />
    );
  };

  /**
   * Determines which view to render based on edit mode state.
   * @returns {JSX.Element} Either edit view or read-only view
   */
  const renderContent = () => {
    return (
      <>
        {renderReadOnlyView()}
        <DialogConfirm
          header={"Delete measurements"}
          text={"Delete these measurements? This action cannot be undone."}
          open={openConfirmDialog}
          onConfirm={() => handleDeleteMeasurements()}
          onClose={() => setOpenConfirmDialog(false)}
        />
      </>
    );
  };

  /**
   * Handles deletion of measurements. Sets the deleted flag and triggers
   * save.
   */
  const handleDeleteMeasurements = () => {
    chartContext.onChange({
      measurementInSitting: { [measurementsInSitting.id]: { deleted: true } },
    });
    chartContext.onChange({
      atpAssessment: {
        [atpAssessment.id]: {
          measurementInSittingId: null,
        },
      },
    });
    setCardChart(null);
    setPendingSave(true);
    setOpenConfirmDialog(false);
  };

  useEffect(() => {
    if (pendingSave) {
      chartContext.handleSaveChart();
      setPendingSave(false);
    }
  }, [pendingSave]);
  /**
   * Returns the action buttons for the card component.
   * @returns {Array<JSX.Element>} Array of action button components
   */
  const getActions = () => {
    const handleCreateMeasurements = () => {
      const newMeasurementId = chartContext.getNegativeId();
      const measurements = {
        [newMeasurementId]: {
          id: newMeasurementId,
          patientId: chartContext.patientId,
          orderActivityId: chartContext.orderId,
          createdById: 1,
          deleted: false,
        },
      };
      chartContext.onChange({ measurementInSitting: measurements });
      setEditMode(true);
    };

    const handleCancel = () => {
      const updatedMeasurements = { ...measurementsInSitting };
      if (updatedMeasurements.id < 0) {
        chartContext.resetChart(chartContext.originalChart);
        chartContext.setCardEditMode?.(className, false);
        setEditMode(false);
        return;
      }
      const measurementsInSittingFields = cardChart
        ? Object.keys(cardChart)
        : [];

      for (const field of measurementsInSittingFields) {
        if (
          measurementsInSitting[field] !== originalMeasurementInSitting[field]
        ) {
          updatedMeasurements[field] = originalMeasurementInSitting[field];
        }
      }
      chartContext.onChange({
        measurementInSitting: {
          [measurementsInSitting.id]: { ...updatedMeasurements },
        },
      });
      chartContext.setCardEditMode?.(className, false);
      setEditMode(false);

      const clearedIssues = { ...chartContext.validationIssues };
      delete clearedIssues[className];
      chartContext.updateValidationIssues(clearedIssues);
    };

    return Object.values(measurementsInSitting).length > 0
      ? [
          ...(props.enableNormalEditMode
            ? [
                ...(editMode
                  ? [
                      <MUIButton
                        color="error"
                        disabled={chartContext.isUpdating}
                        onClick={handleCancel}
                        size="small"
                      >
                        Cancel
                      </MUIButton>,
                    ]
                  : [
                      <MUIButton
                        disabled={chartContext.isUpdating}
                        onClick={() => setEditMode(true)}
                        size="small"
                      >
                        Edit
                      </MUIButton>,
                    ]),
              ]
            : []),
          measurementsInSitting.id > 0 ? (
            <Menu
              menuItems={[
                <MUIMenuItem
                  onClick={() => {
                    if (measurementsInSitting.id < 0) {
                      handleCancel();
                    } else {
                      setOpenConfirmDialog(true);
                    }
                  }}
                >
                  <MUIListIcon>
                    <MUIDeleteIcon />
                  </MUIListIcon>
                  Delete
                </MUIMenuItem>,
              ].filter(Boolean)}
            />
          ) : (
            <MUIBox></MUIBox>
          ),
        ]
      : [
          <MUIButton
            variant={editMode ? "contained" : "outlined"}
            startIcon={editMode ? <MUISaveIcon /> : <MUIAddIcon />}
            disabled={chartContext.isUpdating}
            onClick={() => handleCreateMeasurements()}
          >
            Create
          </MUIButton>,
        ];
  };

  return (
    <Card
      renderContent={renderContent}
      getActions={getActions}
      getTitle={() => title}
      getIcon={() => icon}
    />
  );
}

CardChartMeasurementInSitting.meta = {
  className: className,
  title: title,
  icon: icon,
};
